import { createRouter as _createRouter, createWebHistory } from 'vue-router';
import {
  setupGuards,
  confirmAccountGuard,
  onboardingGuard,
  paymentOnboardingGuard,
  impersonatedGuard,
  beforeProviderEditingGuard,
  beforeLeadsGuard,
} from '@routes/commonRouteGuards';
import careProviderStore from '../stores/care-provider.store';
import CareProviderTours from '../components/tours.vue';

const beforeConfirmAccount = confirmAccountGuard(careProviderStore);
const beforeOnboarding = onboardingGuard(careProviderStore);
const beforePaymentOnboarding = paymentOnboardingGuard(careProviderStore);
const beforeImpersonated = impersonatedGuard(careProviderStore);
const beforeProviderEditing = beforeProviderEditingGuard(careProviderStore);
const beforeLeads = beforeLeadsGuard(careProviderStore);

const Error404 = { template: '<div><h2>Uh oh. Error #404</h2><p>Page not found</p></div>' };

const router = _createRouter({
  history: createWebHistory(),

  routes: [
    {
      path: '/home',
      name: 'home',
      alias: '/',
      component: () => import('../../provider/pages/home.vue'),
      meta: {
        skipFacilitySelector: true,
        renderFacilitySelector: true,
      },
    },
    {
      path: '/onboarding',
      name: 'onboarding',
      component: () => import('../components/onboarding/onboarding.vue'),
      beforeEnter: beforeOnboarding,
    },
    {
      path: '/payment-onboarding',
      name: 'payment-onboarding',
      component: () => import('../components/onboarding/payment_onboarding.vue'),
      beforeEnter: beforePaymentOnboarding,
    },
    {
      name: 'secret-onboarding',
      path: '/secret-provider-onboarding',
      component: () => import('../components/onboarding/onboarding.vue'),
      beforeEnter: beforeImpersonated,
    },
    {
      path: '/confirm-account',
      name: 'confirm-account',
      component: () => import('@components/confirmAccountPage.vue'),
      beforeEnter: beforeConfirmAccount,
    },
    {
      path: '/openings',
      name: 'availability',
      component: () => import('../components/availability/availability-page.vue'),
      beforeEnter: beforeProviderEditing,
    },
    {
      path: '/profile',
      name: 'edit-profile',
      component: () => import('../../provider/pages/profile-editor.vue'),
      meta: {
        skipFacilitySelector: true,
        renderFacilitySelector: true,
      },
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('../../provider/pages/settings.vue'),
      meta: {
        skipFacilitySelector: true,
      },
    },
    {
      path: '/messaging',
      name: 'messaging',
      component: () => import('../components/messaging.vue'),
    },
    {
      path: '/tours',
      component: CareProviderTours,
      children: [
        {
          path: '',
          name: 'toursPage',
          component: () => import('../components/tours/tours.vue'),
        },
        {
          path: 'history',
          name: 'toursHistory',
          component: () => import('../components/tours/history.vue'),
        },
      ],
    },
    {
      path: '/payments',
      component: () => import('../../provider/pages/payments.vue'),
      children: [
        {
          path: '',
          name: 'payments-all',
          component: () => import('../../provider/pages/payments/all.vue'),
        },
        {
          path: 'billing',
          name: 'payments-billing',
          component: () => import('../../provider/pages/payments/billing.vue'),
        },
        {
          path: 'pay',
          name: 'payments-pay-link',
          component: () => import('../../provider/pages/payments/pay-link.vue'),
        },
        {
          path: 'settings',
          name: 'payments-settings',
          component: () => import('../../provider/pages/payments/settings.vue'),
          meta: {
            skipFacilitySelector: true,
            renderFacilitySelector: true,
          },
        },
      ],
    },
    {
      path: '/billing/:id',
      name: 'billing-plan',
      component: () => import('../components/billing-plans/billing-plan.vue'),
      props: (route) => ({ billingPlanId: Number(route.params.id) }),
    },
    {
      path: '/families/:parentId/:childId',
      name: 'families-child',
      component: () => import('../../provider/pages/families/child.vue'),
      meta: {
        skipFacilitySelector: true,
      },
    },
    {
      path: '/families/:parentId',
      name: 'families-parent',
      component: () => import('../../provider/pages/families/parent.vue'),
      meta: {
        skipFacilitySelector: true,
      },
    },
    {
      path: '/families',
      name: 'families',
      component: () => import('../../provider/pages/families.vue'),
      meta: {
        skipFacilitySelector: true,
      },
    },
    {
      path: '/leads',
      name: 'leads',
      component: () => import('../../provider/pages/leads.vue'),
      meta: {
        skipFacilitySelector: true,
      },
      beforeEnter: beforeLeads,
    },
    {
      path: '/programs',
      name: 'programs',
      component: () => import('../../provider/pages/programs.vue'),
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'error404',
      component: Error404,
    },
  ],
});

setupGuards(careProviderStore, router);

export default router;
